import { CurrencySelect } from "@/components/select/CurrencySelect.tsx";
import { useCompensationComponents } from "@/queries/use-compensation-components.query.ts";
import { useEmployeeMe } from "@/queries/employees/use-employees.query.ts";
import { BASIC_SALARY_COMPENSATION_COMPONENT_CODE } from "@/types/compensation-component.types.ts";
import {
  ApiEmployeeCompensationPerTypes,
  getEmployeeCompensationPerTypes,
} from "@/types/employees/employee-compensation.types.ts";
import { ApiFormResultCompensationLine } from "@/types/forms/form.types.ts";
import {
  Box,
  Button,
  ButtonIcon,
  FontAwesomeIcon,
  Input,
  Select,
  Stack,
  Text,
  Title,
} from "@skailer-com/uikit";
import { useEffect } from "react";
import { Controller, useFormContext, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";

interface Props {
  name: string;
}

const emptyLine = (componentId = 0): ApiFormResultCompensationLine => ({
  id: null,
  compensation_component_id: componentId,
  amount_gross: null,
  amount_net: null,
  currency_code: null,
  per_type: null,
  sort: null,
  description: null,
  rate_type: null,
  percent: null,
});

export function WriteToFieldCompensationLines({ name }: Props) {
  const { t } = useTranslation();
  const { control, setValue, getFieldState, formState } = useFormContext();
  const { data: employeeMe } = useEmployeeMe();
  const { data: components = [], isLoading } = useCompensationComponents();
  const value = useWatch({ control, name }) as ApiFormResultCompensationLine[] | undefined;
  const lines = value ?? [];
  const fieldError = getFieldState(name, formState).error?.message;

  useEffect(() => {
    if (isLoading) return;

    if (lines.length === 0) {
      const basicSalary = components.find(
        (component) => component.code === BASIC_SALARY_COMPENSATION_COMPONENT_CODE
      );
      if (!basicSalary) return;
      setValue(
        name,
        [
          {
            ...emptyLine(basicSalary.id),
            currency_code:
              basicSalary.default_currency_code ?? employeeMe?.organization.currency ?? null,
            per_type: basicSalary.default_per_type ?? ApiEmployeeCompensationPerTypes.monthly,
            sort: basicSalary.sort,
          },
        ],
        { shouldValidate: false }
      );
      return;
    }

    let hasMissingDefaults = false;
    const linesWithDefaults = lines.map((line) => {
      if (line.currency_code && line.per_type) return line;
      const component = components.find((item) => item.id === line.compensation_component_id);
      const currencyCode =
        line.currency_code ??
        component?.default_currency_code ??
        employeeMe?.organization.currency ??
        null;
      const perType =
        line.per_type ?? component?.default_per_type ?? ApiEmployeeCompensationPerTypes.monthly;
      hasMissingDefaults ||= currencyCode !== line.currency_code || perType !== line.per_type;
      return {
        ...line,
        currency_code: currencyCode,
        per_type: perType,
      };
    });

    if (hasMissingDefaults) {
      setValue(name, linesWithDefaults, { shouldValidate: false });
    }
  });

  const updateLine = (index: number, patch: Partial<ApiFormResultCompensationLine>) => {
    setValue(
      name,
      lines.map((line, lineIndex) => (lineIndex === index ? { ...line, ...patch } : line)),
      { shouldDirty: true, shouldValidate: true }
    );
  };

  return (
    <Stack gap="sm" className={"py-4"}>
      <Title size="xs" header={t("core:compensation")} />
      {lines.map((line, index) => {
        return (
          <Box key={`${line.id ?? "new"}-${index}`} variant="border" className="bg-dark/5 p-4">
            <Stack gap="sm">
              <Select
                required
                label={t("core:compensation_type")}
                options={components}
                value={line.compensation_component_id || undefined}
                isLoading={isLoading}
                searchable
                emptyTitle={t("common:select")}
                onChange={(componentId) => {
                  const component = components.find((item) => item.id === Number(componentId));
                  updateLine(index, {
                    compensation_component_id: Number(componentId),
                    currency_code:
                      component?.default_currency_code ?? employeeMe?.organization.currency ?? null,
                    per_type:
                      component?.default_per_type ?? ApiEmployeeCompensationPerTypes.monthly,
                    sort: component?.sort ?? line.sort,
                  });
                }}
              />
              <Stack direction="horizontal" gap="md">
                <Input
                  type="number"
                  inputMode="decimal"
                  label={t("core:compensation_line_net")}
                  value={String(line.amount_net ?? "")}
                  onChange={(event) => updateLine(index, { amount_net: event.target.value })}
                />
                <Input
                  type="number"
                  inputMode="decimal"
                  label={t("core:compensation_line_gross")}
                  value={String(line.amount_gross ?? "")}
                  onChange={(event) => updateLine(index, { amount_gross: event.target.value })}
                />
              </Stack>
              <Stack direction="horizontal" gap="sm" items="center">
                <Select
                  required
                  variant="button"
                  options={getEmployeeCompensationPerTypes()}
                  value={line.per_type ?? undefined}
                  emptyTitle={t("common:period")}
                  onChange={(perType) =>
                    updateLine(index, {
                      per_type: perType as ApiEmployeeCompensationPerTypes,
                    })
                  }
                />
                <Text>{t("core:compensation_line_currency_separator")}</Text>
                <CurrencySelect
                  required
                  variant="button"
                  value={line.currency_code ?? undefined}
                  emptyTitle={t("common:currency")}
                  searchable
                  onChange={(currencyCode) =>
                    updateLine(index, { currency_code: String(currencyCode) })
                  }
                />
              </Stack>
              {lines.length > 1 && (
                <Stack direction="horizontal" justify="end">
                  <ButtonIcon
                    variant="plain"
                    onClick={() =>
                      setValue(
                        name,
                        lines.filter((_, lineIndex) => lineIndex !== index),
                        { shouldDirty: true, shouldValidate: true }
                      )
                    }
                    icon={<FontAwesomeIcon icon="fa-light fa-trash" />}
                  />
                </Stack>
              )}
            </Stack>
          </Box>
        );
      })}
      {fieldError && <Text className="text-danger text-sm">{fieldError}</Text>}
      <Stack direction="horizontal">
        <Button
          type="button"
          variant="secondary"
          size="sm"
          leftIcon={<FontAwesomeIcon icon="fa-light fa-plus" />}
          onClick={() => {
            setValue(
              name,
              [
                ...lines,
                {
                  ...emptyLine(),
                  currency_code: employeeMe?.organization.currency ?? null,
                  per_type: ApiEmployeeCompensationPerTypes.monthly,
                },
              ],
              {
                shouldDirty: true,
                shouldValidate: true,
              }
            );
          }}
        >
          {t("core:compensation_add_line")}
        </Button>
      </Stack>
      <Controller control={control} name={name} render={() => <></>} />
    </Stack>
  );
}
